/**
 * API Service: Tiering Settings
 *
 * Encapsulates all HTTP calls for Tiering Periods using the centralized
 * Axios-based apiService. Follows the same pattern as DashboardApiService.
 */

import { config } from "@/config";
import { apiService, type PaginatedApiResponse } from "@/remote/api";

import { mockTieringSettingsData } from "../../../mock";
import type {
  CreateTieringPeriodRequest,
  TieringPeriodDetailDto,
  TieringPeriodDto,
  TieringTierWriteRequest,
  UpdateTieringPeriodRequest,
} from "../response/TieringSettingsResponse";

export interface FetchTieringPeriodsParams {
  readonly page?: number;
  readonly size?: number;
  readonly active?: boolean;
  readonly search?: string;
  readonly sortBy?: "year" | "name";
  readonly sortDir?: "asc" | "desc";
}

export class TieringSettingsApiService {
  private readonly useMockData: boolean;

  constructor() {
    this.useMockData = config.features.useMockData;
  }

  /**
   * Fetch tiering periods with pagination and optional filters.
   * Endpoint: GET /product/api/v1/tiering/periods/paginated
   */
  public async fetchPeriodsPaginated(
    params: FetchTieringPeriodsParams = {},
  ): Promise<PaginatedApiResponse<TieringPeriodDto>> {
    const {
      page = 1,
      size = 10,
      active,
      search,
      sortBy = "year",
      sortDir = "desc",
    } = params;

    if (this.useMockData) {
      const sorted = [...mockTieringSettingsData.data].sort(
        (a, b) => Number.parseInt(b.period, 10) - Number.parseInt(a.period, 10),
      );

      const startIndex = (page - 1) * size;
      const pageItems = sorted.slice(startIndex, startIndex + size);

      const data: TieringPeriodDto[] = pageItems.map((item) => ({
        id: item.id,
        name: `Tiering ${item.period}`,
        period: Number.parseInt(item.period, 10),
        isActive: item.status === "ongoing",
        validFrom: `${item.period}-01-01`,
        validUntil: `${item.period}-12-31`,
        tierCount: 4,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      }));

      const totalRecords = mockTieringSettingsData.data.length;
      const totalPages = Math.max(1, Math.ceil(totalRecords / size));

      return {
        data,
        paging: {
          currentPage: page,
          pageSize: size,
          totalPages,
          totalRecords,
        },
        meta: {
          code: "20000",
          message: "OK (mock tiering periods)",
          timestamp: new Date().toISOString(),
        },
      };
    }

    return apiService.getPaginated<TieringPeriodDto>(
      "/product/api/v1/tiering/periods/paginated",
      {
        params: {
          active,
          search,
          page,
          size,
          sortBy,
          sortDir,
        },
      },
    );
  }

  /**
   * Fetch detailed information for a specific tiering period.
   * Endpoint: GET /product/api/v1/tiering/period/detail?id={period_id}
   */
  public async fetchPeriodDetail(id: string): Promise<TieringPeriodDetailDto> {
    if (this.useMockData) {
      const fallbackYear = mockTieringSettingsData.data[0]?.period ?? "2024";
      const yearNumber = Number.parseInt(fallbackYear, 10);

      return {
        id,
        period: {
          year: yearNumber,
          name: `Tiering ${yearNumber}`,
          valid_from: `${yearNumber}-01-01`,
          valid_until: `${yearNumber}-12-31`,
          is_active: true,
        },
        tiers: [1, 2, 3, 4].map((level) => ({
          id: `${id}-tier-${level}`,
          metadata: {},
          tier_level: level,
          tier_name: ["Bronze", "Silver", "Gold", "Platinum"][level - 1] ?? "Tier",
          tier_code: ["BRONZE", "SILVER", "GOLD", "PLATINUM"][level - 1] ?? "TIER",
          min_points: (level - 1) * 100,
          max_points: level < 4 ? level * 100 : undefined,
          direct_points_multiplier: 1 + (level - 1) * 0.25,
          birthday_reward_points: level * 50,
          daily_redemption_limit: level * 250,
          badge_icon: `/badges/${["bronze", "silver", "gold", "platinum"][
            level - 1
          ]}.png`,
          color_hex: ["#CD7F32", "#C0C0C0", "#FFD700", "#E5E4E2"][level - 1] ?? "#000000",
          is_active: true,
        })),
      };
    }

    const response = await apiService.get<TieringPeriodDetailDto>(
      "/product/api/v1/tiering/period/detail",
      {
        params: { id },
      },
    );

    return response.data;
  }

  /**
   * Create a new tiering period with its tiers.
   * Endpoint: POST /product/api/v1/tiering/period
   */
  public async createPeriod(
    payload: CreateTieringPeriodRequest,
  ): Promise<{ id: string }> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        const generatedId = `mock-period-${Date.now().toString(36)}`;
        setTimeout(() => resolve({ id: generatedId }), 300);
      });
    }

    return apiService.postData<{ id: string }, CreateTieringPeriodRequest>(
      "/product/api/v1/tiering/period",
      payload,
    );
  }

  /**
   * Update an existing tiering period.
   * Endpoint: PUT /product/api/v1/tiering/period/{period_id}
   */
  public async updatePeriod(
    id: string,
    payload: UpdateTieringPeriodRequest,
  ): Promise<void> {
    if (this.useMockData) {
      return Promise.resolve();
    }

    await apiService.putData<void, UpdateTieringPeriodRequest>(
      `/product/api/v1/tiering/period/${id}`,
      payload,
    );
  }

  /**
   * Toggle active status for a tiering period.
   * Endpoint: PATCH /product/api/v1/tiering/period/{period_id}/status?active=true
   */
  public async togglePeriodStatus(id: string, active: boolean): Promise<void> {
    if (this.useMockData) {
      return Promise.resolve();
    }

    await apiService.patchData<void>(
      `/product/api/v1/tiering/period/${id}/status`,
      undefined,
      {
        params: { active },
      },
    );
  }

  /**
   * Delete a tiering period.
   * Endpoint: DELETE /product/api/v1/tiering/period/{period_id}
   */
  public async deletePeriod(id: string): Promise<void> {
    if (this.useMockData) {
      return Promise.resolve();
    }

    await apiService.deleteData<void>(`/product/api/v1/tiering/period/${id}`);
  }

  /**
   * Helper to build a write payload from simple values.
   * This keeps form-to-API mapping logic centralized and testable.
   */
  public buildWritePayload(
    year: number,
    name: string,
    isActive: boolean,
    tiers: TieringTierWriteRequest[],
  ): CreateTieringPeriodRequest {
    const safeYear = Number.isFinite(year) ? year : new Date().getFullYear();

    return {
      year: safeYear,
      name,
      validFrom: `${safeYear}-01-01`,
      validUntil: `${safeYear}-12-31`,
      isActive,
      tiers,
    };
  }
}
